import React from "react";
import { StyleSheet, View } from "react-native";
import { Select as NBSelect, Text } from "native-base";
import { Platform } from "react-native";

import { FilterKey } from "../../../../../types";
import PlatformSelect from "../../../../../../Universal/Inputs/PlatformSelect";
import { useAppContext } from "../../../../../../Universal/API/Context-API/AppContext";
import { webFont } from "../../../../../../Universal/API/API";

type FilterSectionDropdownProps = {
  title: string;
  filterKey: FilterKey;
  options: string[];
  selectedValues: string[];
  unavailableSet: Set<string>;
  onToggle: (key: FilterKey, value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  clearLabel?: string;
};

const FilterSectionDropdown: React.FC<FilterSectionDropdownProps> = ({
  title,
  filterKey,
  options,
  selectedValues,
  unavailableSet,
  onToggle,
  disabled = false,
  placeholder,
  clearLabel,
}) => {
  const { scaleAll } = useAppContext();
  const styles = React.useMemo(() => createStyles(scaleAll), [scaleAll]);
  const selectedDropdownValue = selectedValues[0] ?? "";
  const dropdownPlaceholder = placeholder ?? "Выберите значение";
  const dropdownClearLabel = clearLabel ?? "Все";

  const longestDropdownLabel = React.useMemo(
    () =>
      options.reduce(
        (max, option) =>
          Math.max(max, option.length, dropdownPlaceholder.length, dropdownClearLabel.length),
        0
      ),
    [dropdownClearLabel.length, dropdownPlaceholder.length, options]
  );

  const dropdownWidth = React.useMemo(() => {
    if (Platform.OS === "web") {
      const widthInCh = Math.min(Math.max(longestDropdownLabel + 7, 18), 34);
      return `${widthInCh}ch`;
    }
    return 280;
  }, [longestDropdownLabel]);

  return (
    <View style={styles.wrap}>
      <Text selectable={false} style={styles.title}>
        {title}
      </Text>

      <PlatformSelect
        selectedValue={selectedDropdownValue}
        width={dropdownWidth}
        maxWidth={dropdownWidth}
        accessibilityLabel={title}
        placeholder={dropdownPlaceholder}
        isDisabled={disabled}
        onValueChange={(value) => {
          if (value === "__clear__") {
            if (selectedDropdownValue) onToggle(filterKey, selectedDropdownValue);
            return;
          }

          onToggle(filterKey, value);
        }}
      >
        <NBSelect.Item label={dropdownClearLabel} value="__clear__" />
        {options.map((opt) => {
          const isUnavailable = unavailableSet.has(opt.toLowerCase());
          const label = opt.charAt(0).toUpperCase() + opt.slice(1);

          return (
            <NBSelect.Item
              key={`${filterKey}:${opt}`}
              label={label}
              value={opt}
              isDisabled={isUnavailable}
            />
          );
        })}
      </PlatformSelect>
    </View>
  );
};

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    wrap: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10 * scaleAll,
    },
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#6B665F",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.7,
    },
  });

export default React.memo(FilterSectionDropdown);
